import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
    createColumnHelper,
    getCoreRowModel,
    useReactTable,
    SortingState,
    getSortedRowModel,
    Row as RowType,
} from '@tanstack/react-table';
import CardWithDivider from '../../../../components/Layout/CardWithDivider';
import CardTitle from '../../../../components/typography/CardTitle';
import { Button } from '@ourliving/ourliving-ui';
import { L } from '../../../../lib/i18n';
import EmptyState from '../../../../components/EmptyStateCommunity/EmptyStateCommunity';
import { useNavigate } from 'react-router-dom-v5-compat';
import { useGetInfinteSuppliers } from '../hooks/supplierQueries';
import { Supplier } from '@app/trpc-api/models/Suppliers';
import dayjs from 'dayjs';
import TopAreaContainer from '../../../binders/components/TopAreaContainer';
import GenericTableVirtualized from '../../../binders/components/GenericTableVirtualizedInfinite';
import FilterSearchContainer from '../../../binders/components/emotion/Filters/FilterSearchContainer';
import PaddedContainer from '../../../binders/components/emotion/PaddedContainer';
import Row from '../../../binders/components/emotion/Row';
import GlobalFilter from '../../../../components/Table/FilterComponents/GlobalFilterTable';
import { Checkbox } from '../../../../components/Checkbox/Checkbox';
import { margin } from '../../../../components/Shared/Style/margin';
import ConfirmationButton from '../../../../components/Buttons/ConfirmButton';
import useDeleteSuppliers from '../hooks/useDeleteSuppliers';
import toast from 'react-hot-toast';

const CellContainer = styled.div({
    display: 'grid',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    maxWidth: '100%',
    fontSize: '0.75rem',
    gap: '0.2rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const Flex = styled.div({
    display: 'flex',
});

const Data = styled.p({
    margin: '0px',
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const columnHelper = createColumnHelper<Supplier>();

const SupplierList = ({ handleSetOpen }: { handleSetOpen: () => void }) => {
    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'selection',
                header: ({ table }) => (
                    <Checkbox
                        checked={table.getIsAllRowsSelected()}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                    />
                ),
                cell: ({ row }) => (
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <Checkbox
                            checked={row.getIsSelected()}
                            disabled={!row.getCanSelect()}
                            onChange={row.getToggleSelectedHandler()}
                        />
                    </div>
                ),
                size: 30,
                meta: {
                    styles: {
                        flex: '0 0 auto',
                    },
                },
                enableResizing: false,
            }),
            columnHelper.accessor((row) => row.name, {
                id: 'name',
                cell: (info) => (
                    <CellContainer>
                        <Data>{info.getValue()}</Data>
                    </CellContainer>
                ),
                header: () => <Flex>{L('name')}</Flex>,
                meta: {
                    styles: {
                        flex: '4 0 auto',
                        width: 150,
                    },
                },
            }),
            columnHelper.accessor((row) => row.created_at, {
                id: 'created_at',
                cell: (info) => (
                    <CellContainer>
                        <Data>{dayjs(info.row.original.created_at).format('YYYY-MM-DD HH:mm')}</Data>
                    </CellContainer>
                ),
                header: () => <Flex>{L('created_at')}</Flex>,
                meta: {
                    styles: {
                        width: 120,
                    },
                },
            }),
            columnHelper.accessor((row) => row.updated_at, {
                id: 'updated_at',
                cell: (info) => (
                    <CellContainer>
                        <Data>{dayjs(info.row.original.updated_at).format('YYYY-MM-DD HH:mm')}</Data>
                    </CellContainer>
                ),
                header: () => <Flex>{L('updated_at')}</Flex>,
                meta: {
                    styles: {
                        width: 120,
                        marginLeft: 'auto',
                    },
                },
            }),
        ],
        [],
    );

    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

    const navigate = useNavigate();

    const handleClickRow = useCallback((row: RowType<Supplier>) => {
        navigate(`${location.pathname}/${row.original.id}`);
    }, []);

    const searchParams = new URLSearchParams(location.search);

    const searchField = useMemo(() => searchParams.get('searchField') ?? '', [searchParams]);

    const handleSearch = (value: string) => {
        searchParams.set('searchField', value);
        navigate({
            search: searchParams.toString(),
        });
    };

    const [sorting, setSorting] = useState<SortingState>([]);

    const { fetchNextPage, hasNextPage, isFetching, data } = useGetInfinteSuppliers({
        keepPreviousData: true,
        search: searchField,
        sorting,
    });

    const flatData = useMemo(() => data?.pages?.flatMap((page) => page.items) ?? [], [data]);

    const totalDBRowCount = useMemo(() => Number(data?.pages?.[0]?.totalItems) ?? 0, [data]);
    const totalFetched = useMemo(() => flatData.length, [flatData]);

    const table = useReactTable({
        data: flatData,
        columns,
        manualSorting: true,
        state: {
            sorting,
            rowSelection,
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: setSorting,
        enableColumnResizing: false,
        onRowSelectionChange: setRowSelection,
    });
    const { mutate: deleteSuppliers } = useDeleteSuppliers();

    const handleRemoveSupplier = (suppliers: Supplier[]) => {
        const ids = suppliers.map((supplier) => supplier.id);
        deleteSuppliers(ids, {
            onSuccess: () => {
                setRowSelection({});
                if (ids.length === 1) toast.success(L('removed_supplier'));
                if (ids.length > 1) toast.success(L('removed_suppliers'));
            },
            onError: () => {
                toast.error(L('an_error_occurred'));
            },
        });
    };

    useEffect(() => {
        setRowSelection({});
    }, [data]);

    const selectedSuppliers = table.getSelectedRowModel().flatRows.map((data) => data.original);

    return (
        <CardWithDivider
            topArea={
                <TopAreaContainer>
                    <CardTitle>{L('suppliers')}</CardTitle>
                    <div style={{ display: 'flex', columnGap: margin.s, marginLeft: 'auto' }}>
                        <ConfirmationButton
                            props={{
                                onConfirm: () => {
                                    handleRemoveSupplier(selectedSuppliers);
                                },
                                confirmVariant: 'danger',
                            }}
                            disabled={!Boolean(selectedSuppliers.length > 0)}
                        >
                            {L('remove')}
                        </ConfirmationButton>
                        <Button style={{ marginLeft: 'auto' }} onClick={() => handleSetOpen()}>
                            {L('new_supplier')}
                        </Button>
                    </div>
                </TopAreaContainer>
            }
            mainArea={
                <>
                    <FilterSearchContainer style={{ justifyContent: 'end' }}>
                        <PaddedContainer>
                            <Row>
                                <GlobalFilter
                                    style={{ marginLeft: 'auto' }}
                                    filter={searchField ?? ''}
                                    setFilter={handleSearch}
                                    placeholder={L('search')}
                                />
                            </Row>
                        </PaddedContainer>
                    </FilterSearchContainer>

                    <GenericTableVirtualized
                        table={table}
                        handleFetchNextPage={() => fetchNextPage()}
                        hasNextPage={hasNextPage}
                        isFetching={isFetching}
                        totalDBRowCount={totalDBRowCount}
                        totalFetched={totalFetched}
                        estimateSize={50}
                        rowClickHandler={handleClickRow}
                    />
                    {flatData?.length === 0 &&
                        data &&
                        (!searchField ? (
                            <EmptyState>
                                <EmptyState.Title>{L('no_suppliers')}</EmptyState.Title>
                                <EmptyState.Description>
                                    <p>{L('no_suppliers_description')}</p>
                                    <p>{L('click_button_below')}</p>
                                    <Button onClick={() => handleSetOpen()}> {L('new_supplier')}</Button>
                                </EmptyState.Description>
                            </EmptyState>
                        ) : (
                            <EmptyState>
                                <EmptyState.Title>{L('no_suppliers_found')}</EmptyState.Title>
                                <EmptyState.Description>
                                    <p>{L('no_suppliers_found_description')}</p>
                                    <p>{L('please_try_something_else')}</p>
                                </EmptyState.Description>
                            </EmptyState>
                        ))}
                </>
            }
        />
    );
};

export default SupplierList;
